import type {
  AlertAck,
  ComponentBlock,
  DeliveryPlatform,
  Ingredient,
  IngredientCost,
  MenuEngineeringClass,
  MenuEngineeringItem,
  MenuItem,
  PricingAnalysis,
  PricingStatus,
  Recipe,
  RecipeComponent,
  RecipeCost,
  RecipeCostLine,
  TenantSettings,
} from './types';
import {
  BASE_UNIT,
  dimensionOf,
  round,
  toBase,
  Unit,
  UnitError,
  UNITS,
  type CostingCulprit,
} from './units';

export class CostingError extends Error {
  /** Que hay que abrir para arreglarlo. Ver `CostingCulprit`. */
  readonly culprit?: CostingCulprit;

  constructor(message: string, culprit?: CostingCulprit) {
    super(message);
    this.name = 'CostingError';
    this.culprit = culprit;
  }
}

/* ------------------------------------------------------------------ */
/* Ingredientes                                                        */
/* ------------------------------------------------------------------ */

/**
 * Nombre completo del ingrediente tal y como aparece en las recetas:
 * "Aceite de oliva virgen extra Oro Bailén 1 L".
 */
export function ingredientDisplayName(ing: Ingredient): string {
  return [ing.name, ing.brand, `${trimNumber(ing.purchaseQty)} ${UNITS[ing.purchaseUnit].label}`]
    .filter(Boolean)
    .join(' ');
}

function trimNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(round(value, 3));
}

/**
 * El formato de compra tal y como se pide y se cuenta: "Garrafa de 15 L",
 * "Caja de 24 pz", "Saco de 25 kg".
 *
 * Es como se hace un inventario de verdad. Nadie mira una estanteria y apunta
 * 37,5 litros: apunta dos garrafas y media. Sin presentacion cargada se queda
 * en el formato a secas ("15 L"), que sigue siendo mas util que la unidad
 * suelta (Alex, 2 sep 2026).
 */
export function purchaseFormatLabel(
  ing: Pick<Ingredient, 'presentation' | 'purchaseQty' | 'purchaseUnit'>,
): string {
  const unidad = UNITS[ing.purchaseUnit].label;
  const formato = `${trimNumber(ing.purchaseQty)} ${unidad}`;
  const presentacion = ing.presentation?.trim();
  if (!presentacion) return formato;
  // "Kg de 1 kg" no es un formato de compra, es un trabalenguas: cuando la
  // presentacion es la propia unidad, con el formato basta.
  if (presentacion.toLocaleLowerCase('es') === unidad.toLocaleLowerCase('es')) return formato;
  const nombre = presentacion.charAt(0).toLocaleUpperCase('es') + presentacion.slice(1);
  return `${nombre} de ${formato}`;
}

/* ------------------------------------------------------------------ */
/* Precio de compra                                                    */
/* ------------------------------------------------------------------ */

/**
 * Conversion entre el precio sin IVA y el precio con IVA.
 *
 * El unico valor que se guarda es el NETO. Estas dos funciones son las que
 * usan el formulario y el importador para dejar teclear el que el usuario
 * tenga delante (albaran sin IVA, tique con IVA) y quedarse siempre con el
 * mismo dato en base de datos.
 */
export function grossFromNet(net: number, vatPct: number): number {
  return round(net * (1 + safeVat(vatPct) / 100), 4);
}

export function netFromGross(gross: number, vatPct: number): number {
  return round(gross / (1 + safeVat(vatPct) / 100), 4);
}

const safeVat = (vatPct: number): number =>
  Number.isFinite(vatPct) && vatPct > 0 ? vatPct : 0;

/**
 * Precio de compra vigente, sin IVA, o `undefined` si el ingrediente no
 * tiene ninguno.
 *
 * `netPrice` es el campo canonico. `prices` es el vestigio de las tres
 * ofertas de la plantilla: los ingredientes dados de alta antes del cambio
 * siguen guardados asi, y hasta que se vuelvan a guardar se leen promediando
 * como se hacia entonces, para que ningun escandallo cambie de numero solo
 * porque hemos cambiado el modelo.
 */
export function netPriceOf(ing: Pick<Ingredient, 'netPrice' | 'prices'>): number | undefined {
  if (typeof ing.netPrice === 'number' && Number.isFinite(ing.netPrice) && ing.netPrice > 0) {
    return ing.netPrice;
  }
  const legacy = (ing.prices ?? []).filter(
    (p) => typeof p === 'number' && Number.isFinite(p) && p > 0,
  );
  if (legacy.length === 0) return undefined;
  return round(legacy.reduce((a, b) => a + b, 0) / legacy.length, 4);
}

/**
 * Coste de compra por unidad base.
 *
 * El precio de referencia es el precio vigente SIN IVA. El IVA soportado se
 * deduce, asi que no es coste; la merma tampoco entra aqui, porque vive en la
 * linea de la receta (la albahaca merma 35 % en el pesto y 0 % de guarnicion).
 *
 * La plantilla guarda el coste unitario en la UNIDAD DE COMPRA
 * (Master!H = "Coste medio de compra" / "Formato de compra", o sea EUR/kg,
 * EUR/L o EUR/pz) y obliga a que la linea de receta se exprese en esa misma
 * unidad, porque la columna "Unidad" es un INDEX contra Master y no se puede
 * teclear. Nosotros normalizamos a la unidad base (g / mL / pz):
 *
 *   0,195 kg x 17,70 EUR/kg  ==  195 g x 0,0177 EUR/g
 *
 * es la misma multiplicacion, solo cambia donde vive el factor 1000, asi que
 * el coste de la linea sale identico al de Excel. Lo que ganamos es poder
 * escribir la receta en gramos aunque el producto se compre en kilos.
 */
export function computeIngredientCost(ing: Ingredient): IngredientCost {
  const netPrice = netPriceOf(ing);
  // `culprit`: donde se arregla. Aqui, en la ficha del propio ingrediente.
  const culpable: CostingCulprit = {
    kind: 'ingredient',
    id: ing.ingredientId,
    name: ing.name,
  };
  if (netPrice === undefined) {
    throw new CostingError(
      `El ingrediente "${ing.name}" no tiene ningún precio de compra cargado.`,
      culpable,
    );
  }

  const baseQty = toBase(ing.purchaseQty, ing.purchaseUnit);
  if (baseQty <= 0) {
    throw new CostingError(
      `El ingrediente "${ing.name}" tiene un formato de compra inválido (${ing.purchaseQty} ${ing.purchaseUnit}).`,
      culpable,
    );
  }

  return {
    ingredientId: ing.ingredientId,
    name: ing.name,
    displayName: ingredientDisplayName(ing),
    netPrice: round(netPrice, 4),
    grossPrice: grossFromNet(netPrice, ing.vatPct),
    costPerBaseUnit: netPrice / baseQty,
    baseUnit: BASE_UNIT[dimensionOf(ing.purchaseUnit)],
    // El mismo coste dicho en la unidad en la que se compra de verdad.
    costPerPurchaseUnit: netPrice / ing.purchaseQty,
    purchaseUnit: ing.purchaseUnit,
  };
}

/* ------------------------------------------------------------------ */
/* Escandallo                                                          */
/* ------------------------------------------------------------------ */

export interface CostingContext {
  ingredients: Map<string, Ingredient>;
  recipes: Map<string, Recipe>;
}

export function buildContext(ingredients: Ingredient[], recipes: Recipe[]): CostingContext {
  return {
    ingredients: new Map(ingredients.map((i) => [i.ingredientId, i])),
    recipes: new Map(recipes.map((r) => [r.recipeId, r])),
  };
}

interface InternalCost extends RecipeCost {
  /** Coste por unidad base del rendimiento, para encadenar subrecetas */
  costPerBaseUnitOfYield: number;
}

/**
 * Escandallo completo, resolviendo subrecetas de forma recursiva.
 *
 *   Coste linea      = (cantidad neta / (1 - merma)) x coste unitario
 *   Subtotal         = lineas de ingredientes + lineas de empaque
 *   Coste total      = Subtotal x (1 + margen de seguridad)
 *   Coste / racion   = Coste total / raciones
 *   Coste / rendim.  = Coste total / cantidad de rendimiento
 *
 * Detecta referencias circulares en vez de colgarse, que es justo lo que le
 * pasa a una hoja de calculo cuando dos subrecetas se referencian entre si.
 */
export function computeRecipeCost(
  recipeId: string,
  ctx: CostingContext,
  stack: string[] = [],
  memo: Map<string, InternalCost> = new Map(),
): RecipeCost {
  return computeInternal(recipeId, ctx, stack, memo);
}

function computeInternal(
  recipeId: string,
  ctx: CostingContext,
  stack: string[],
  memo: Map<string, InternalCost>,
): InternalCost {
  const cached = memo.get(recipeId);
  if (cached) return cached;

  if (stack.includes(recipeId)) {
    const chain = [...stack, recipeId].map((id) => ctx.recipes.get(id)?.name ?? id).join(' → ');
    throw new CostingError(`Referencia circular entre subrecetas: ${chain}`);
  }

  const recipe = ctx.recipes.get(recipeId);
  if (!recipe) throw new CostingError(`Receta no encontrada: ${recipeId}`);

  const warnings: string[] = [];
  const lines: RecipeCostLine[] = [];
  const nextStack = [...stack, recipeId];

  for (const component of recipe.components) {
    lines.push(costComponent(component, recipe, ctx, nextStack, memo, warnings));
  }

  const costOf = (block: ComponentBlock) =>
    lines.filter((l) => l.block === block).reduce((s, l) => s + l.cost, 0);

  const ingredientCost = costOf('ingrediente');
  const packagingCost = costOf('empaque');
  const subtotal = ingredientCost + packagingCost;

  for (const line of lines) {
    line.weightPct = subtotal > 0 ? round((line.cost / subtotal) * 100, 2) : 0;
  }
  lines.sort((a, b) => (a.block === b.block ? b.cost - a.cost : a.block === 'ingrediente' ? -1 : 1));

  const safetyMarginPct = clampPct(recipe.safetyMarginPct ?? 0, recipe.name, 'margen de seguridad');
  const safetyMarginAmount = subtotal * (safetyMarginPct / 100);
  const totalBatchCost = subtotal + safetyMarginAmount;

  const portions = recipe.portions > 0 ? recipe.portions : 1;
  if (recipe.portions <= 0) warnings.push('La receta no tiene porciones definidas; se ha asumido 1.');

  const yieldBase = toBase(recipe.yieldQty, recipe.yieldUnit);
  if (yieldBase <= 0) {
    warnings.push('El rendimiento del batch es 0: esta receta no se puede usar como subreceta.');
  }
  if (lines.length === 0) warnings.push('La receta no tiene ningún ingrediente.');

  // Se derivan del total YA redondeado: si el duenyo divide el coste total
  // que ve en pantalla entre las raciones, tiene que darle el mismo numero.
  const roundedTotal = round(totalBatchCost, 4);
  const costPerPortion = roundedTotal / portions;
  const costPerYieldUnit = recipe.yieldQty > 0 ? roundedTotal / recipe.yieldQty : 0;

  const result: InternalCost = {
    recipeId: recipe.recipeId,
    name: recipe.name,
    lines,
    ingredientCost: round(ingredientCost, 4),
    packagingCost: round(packagingCost, 4),
    subtotal: round(subtotal, 4),
    safetyMarginPct,
    safetyMarginAmount: round(safetyMarginAmount, 4),
    totalBatchCost: round(totalBatchCost, 4),
    portions,
    costPerPortion: round(costPerPortion, 4),
    costPerYieldUnit: round(costPerYieldUnit, 4),
    yieldQty: recipe.yieldQty,
    yieldUnit: recipe.yieldUnit,
    baseCost: round(recipe.useYieldCostAsBase ? costPerYieldUnit : costPerPortion, 4),
    warnings,
    costPerBaseUnitOfYield: yieldBase > 0 ? totalBatchCost / yieldBase : 0,
  };

  memo.set(recipeId, result);
  return result;
}

function clampPct(pct: number, name: string, label: string): number {
  if (!Number.isFinite(pct) || pct < 0 || pct >= 100) {
    throw new CostingError(
      `El ${label} de "${name}" debe estar entre 0 y 100 (recibido: ${pct}).`,
    );
  }
  return pct;
}

function costComponent(
  component: RecipeComponent,
  parent: Recipe,
  ctx: CostingContext,
  stack: string[],
  memo: Map<string, InternalCost>,
  warnings: string[],
): RecipeCostLine {
  const wastePct = component.block === 'empaque' ? 0 : (component.wastePct ?? 0);
  if (!Number.isFinite(wastePct) || wastePct < 0 || wastePct >= 100) {
    throw new CostingError(
      `La merma de una línea de "${parent.name}" debe estar entre 0 y 100 (recibido: ${wastePct}). ` +
        `Una merma del 100 % significaría que no queda producto.`,
    );
  }
  if (component.qty <= 0) {
    warnings.push(`Una línea de "${parent.name}" tiene cantidad 0.`);
  }

  // Cantidad bruta: hay que comprar mas de lo que acaba en el plato.
  const grossQty = component.qty > 0 ? component.qty / (1 - wastePct / 100) : component.qty;

  if (component.ref.kind === 'ingredient') {
    const ing = ctx.ingredients.get(component.ref.ingredientId);
    if (!ing) {
      // Con culpable: el aviso de la carta lleva a Ingredientes, que es donde
      // se arregla (mejora 59 de Alex: «Ver en la carta» no llevaba a nada).
      throw new CostingError(
        `La receta "${parent.name}" usa un ingrediente que ya no existe (${component.ref.ingredientId}).`,
        { kind: 'recipe', id: parent.recipeId, name: parent.name },
      );
    }
    const cost = computeIngredientCost(ing);
    const grossInBase = convertForIngredient(grossQty, component.unit, ing, parent.name);

    return {
      ref: component.ref,
      block: component.block,
      name: cost.displayName,
      qty: component.qty,
      unit: component.unit,
      wastePct,
      grossQty: round(grossQty, 4),
      unitCost: round(cost.costPerBaseUnit, 6),
      // El coste va por la unidad base DEL INGREDIENTE, que puede no ser la
      // de la linea (aceite comprado en L y recetado en g).
      unitCostUnit: cost.baseUnit,
      cost: round(grossInBase * cost.costPerBaseUnit, 4),
      weightPct: 0,
    };
  }

  const sub = computeInternal(component.ref.recipeId, ctx, stack, memo);
  const subRecipe = ctx.recipes.get(component.ref.recipeId)!;

  // Como se valora una subreceta consumida dentro de otra receta lo decide el
  // toggle B9 de SU PROPIA hoja. La plantilla lo implementa dando de alta cada
  // subreceta como una fila de "Ingredientes" (filas 820-999):
  //
  //   Unidad = IF('Subreceta N'!B9, 'Subreceta N'!H4, "pz")
  //   Precio = IF(Unidad="pz", 'Subreceta N'!L5, 'Subreceta N'!L4)
  //   Formato de compra = 1  ->  Master!H = Precio / 1
  //
  // es decir:
  //   B9 = TRUE  -> la unidad es la del rendimiento (kg, L...) y el precio es
  //                 L4 = coste total / cantidad de rendimiento.
  //   B9 = FALSE -> la unidad es "pz" y el precio es L5 = coste / racion, o
  //                 sea que la cantidad de la linea son RACIONES, no kg ni L.
  if (!subRecipe.useYieldCostAsBase) {
    if (dimensionOf(component.unit) !== 'count') {
      /*
       * El problema esta en la SUBRECETA, no en el plato: es ella la que hay
       * que marcar como "coste por rendimiento". Mandar al usuario a editar el
       * plato —que es lo que se hacia— le deja en una pantalla donde no puede
       * arreglar nada.
       */
      throw new UnitError(
        `"${parent.name}" usa ${component.qty} ${UNITS[component.unit].label} de la subreceta ` +
          `"${subRecipe.name}", pero esa subreceta se valora por ración. La cantidad tiene que ` +
          `ir en piezas (número de porciones) o hay que marcarla como "coste por rendimiento".`,
        { kind: 'recipe', id: subRecipe.recipeId, name: subRecipe.name },
      );
    }
    // toBase sobre una unidad de recuento devuelve piezas (docena -> 12 pz).
    const grossPortions = toBase(grossQty, component.unit);

    return {
      ref: component.ref,
      block: component.block,
      name: subRecipe.name,
      qty: component.qty,
      unit: component.unit,
      wastePct,
      grossQty: round(grossQty, 4),
      unitCost: round(sub.costPerPortion, 6),
      unitCostUnit: 'pz',
      cost: round(grossPortions * sub.costPerPortion, 4),
      weightPct: 0,
    };
  }

  if (dimensionOf(component.unit) !== dimensionOf(subRecipe.yieldUnit)) {
    throw new UnitError(
      `"${parent.name}" usa ${component.qty} ${UNITS[component.unit].label} de la subreceta ` +
        `"${subRecipe.name}", pero esa subreceta se produce en ${UNITS[subRecipe.yieldUnit].label}. ` +
        `Ajusta las unidades.`,
      { kind: 'recipe', id: subRecipe.recipeId, name: subRecipe.name },
    );
  }

  const grossInBase = toBase(grossQty, component.unit);

  return {
    ref: component.ref,
    block: component.block,
    name: subRecipe.name,
    qty: component.qty,
    unit: component.unit,
    wastePct,
    grossQty: round(grossQty, 4),
    unitCost: round(sub.costPerBaseUnitOfYield, 6),
    unitCostUnit: BASE_UNIT[dimensionOf(subRecipe.yieldUnit)],
    cost: round(grossInBase * sub.costPerBaseUnitOfYield, 4),
    weightPct: 0,
  };
}

/** Convierte a la unidad base del ingrediente, usando densidad si hace falta. */
function convertForIngredient(qty: number, unit: Unit, ing: Ingredient, recipeName: string): number {
  const componentDim = dimensionOf(unit);
  const purchaseDim = dimensionOf(ing.purchaseUnit);
  if (componentDim === purchaseDim) return toBase(qty, unit);

  const needsDensity = `"${recipeName}" mide "${ing.name}" en ${UNITS[unit].label} pero se compra en ${UNITS[ing.purchaseUnit].label}. Define la densidad (g/mL) del ingrediente para poder convertir.`;
  // La densidad y el formato de compra se arreglan en la ficha del ingrediente.
  const culpable: CostingCulprit = {
    kind: 'ingredient',
    id: ing.ingredientId,
    name: ing.name,
  };

  if (componentDim === 'volume' && purchaseDim === 'mass') {
    if (!ing.densityGPerMl) throw new UnitError(needsDensity, culpable);
    return toBase(qty, unit) * ing.densityGPerMl;
  }
  if (componentDim === 'mass' && purchaseDim === 'volume') {
    if (!ing.densityGPerMl) throw new UnitError(needsDensity, culpable);
    return toBase(qty, unit) / ing.densityGPerMl;
  }

  throw new UnitError(
    `No se puede convertir ${UNITS[unit].label} a ${UNITS[ing.purchaseUnit].label} para "${ing.name}". ` +
      `Las piezas no se convierten a peso ni a volumen automáticamente.`,
    culpable,
  );
}

/* ------------------------------------------------------------------ */
/* Precios, comisiones y margen                                        */
/* ------------------------------------------------------------------ */

/**
 * Comision extraida del PVP igual que hace la plantilla: PVP - PVP/(1+tasa).
 * No es exactamente PVP x tasa; se replica la formula de Alex para que los
 * numeros del software cuadren con los que sus clientes ya conocen.
 */
export function commissionOnGross(gross: number, pct: number): number {
  if (!pct) return 0;
  const rate = pct / 100;
  return gross - gross / (1 + rate);
}

/**
 * Analisis de precio de un plato de la carta.
 *
 * Lo que diferencia esto de un food cost de manual: las comisiones. Un plato
 * al 30 % de food cost puede perder dinero en una plataforma que se lleva el
 * 30 % del ticket. Aqui la comision entra en el margen siempre.
 *
 * Columna a columna contra la hoja "Precios" (fila 14):
 *   F  Precio sin IVA      = E-(E-(E/(1+IVA)))        = PVP / (1+IVA)
 *   G  Comision bancaria   = IF(plataforma, 0, E-E/(1+banco))
 *   I  % coste             = H/(F-G)   <- el denominador SI descuenta la
 *                            comision bancaria y NO la de plataforma
 *   K  Desviacion          = 1-(coste teorico/coste actual)   (+ = ha subido)
 *   M  Comision plataforma = E-(E/(1+plataforma))
 *   N  Margen bruto        = F-G-H-M
 *   O  % margen bruto      = N/F
 *
 * Coste actual (H): la plantilla escribe
 *   =IF(AND('Receta N'!b8; 'Receta N'!b8<>""); 'Receta N'!L4; 'Receta N'!L5)
 * pero el toggle "usar coste por rendimiento" vive en B9, no en B8. B8 esta
 * siempre vacio, asi que la condicion es siempre falsa y la plantilla acaba
 * usando SIEMPRE L5 = coste / racion. Usamos costPerPortion, que reproduce el
 * numero que el cliente ve hoy en su Excel. (`RecipeCost.baseCost` si respeta
 * el toggle, para quien quiera la lectura por rendimiento.)
 */
export function analyzePricing(
  item: MenuItem,
  recipeCost: RecipeCost,
  settings: TenantSettings,
): PricingAnalysis {
  const vatPct = item.vatPct ?? settings.vatPct ?? 0;
  // PVP del canal: si el analisis es "en tal plataforma" y el plato tiene un
  // PVP propio alli, manda ese; si no, hereda el de comedor.
  const gross = channelPriceOf(item, item.platformId);
  const sellingPriceNet = gross / (1 + vatPct / 100);
  const cost = recipeCost.costPerPortion;

  const platform = item.platformId
    ? settings.platforms.find((p) => p.platformId === item.platformId)
    : undefined;

  // En venta por plataforma no hay datafono: la comision es la de la plataforma.
  const bankCommission = platform ? 0 : commissionOnGross(gross, settings.bankCommissionPct ?? 0);
  const platformCommission = platform ? commissionOnGross(gross, platform.commissionPct) : 0;

  const netAfterBank = sellingPriceNet - bankCommission;
  const foodCostPct = netAfterBank > 0 ? (cost / netAfterBank) * 100 : 0;

  const grossMargin = sellingPriceNet - bankCommission - platformCommission - cost;
  const grossMarginPct = sellingPriceNet > 0 ? (grossMargin / sellingPriceNet) * 100 : 0;

  const costDeviationPct =
    item.theoreticalCost && cost > 0
      ? round((1 - item.theoreticalCost / cost) * 100, 2)
      : undefined;

  const target = item.targetFoodCostPct > 0 ? item.targetFoodCostPct : 30;
  const { suggestedPriceGross, suggestedPriceNet } = suggestPrice({
    cost,
    targetFoodCostPct: target,
    vatPct,
    commissionPct: platform ? platform.commissionPct : (settings.bankCommissionPct ?? 0),
  });

  const deviation = foodCostPct - target;
  const status: PricingStatus =
    deviation <= 2 ? 'ok' : deviation <= 8 ? 'ajustado' : 'infraprecio';

  return {
    menuItemId: item.menuItemId,
    name: item.name,
    costPerPortion: round(cost, 4),
    sellingPriceGross: round(gross, 2),
    sellingPriceNet: round(sellingPriceNet, 4),
    vatPct,
    bankCommission: round(bankCommission, 4),
    platformId: platform?.platformId,
    platformName: platform?.name,
    platformCommission: round(platformCommission, 4),
    foodCostPct: round(foodCostPct, 2),
    grossMargin: round(grossMargin, 4),
    grossMarginPct: round(grossMarginPct, 2),
    markupFactor: cost > 0 ? round(sellingPriceNet / cost, 2) : 0,
    theoreticalCost: item.theoreticalCost,
    costDeviationPct,
    suggestedPriceNet: round(suggestedPriceNet, 2),
    suggestedPriceGross: round(suggestedPriceGross, 2),
    priceGapGross: round(suggestedPriceGross - gross, 2),
    status,
    alerts: buildAlerts({ grossMarginPct, costDeviationPct, settings, platform }),
  };
}

/**
 * PVP que cumple el objetivo de food cost DESPUES de comisiones.
 *
 *   neto - comision = coste / objetivo
 *   PVP x [ 1/(1+IVA) - c/(1+c) ] = coste / objetivo
 */
function suggestPrice(input: {
  cost: number;
  targetFoodCostPct: number;
  vatPct: number;
  commissionPct: number;
}): { suggestedPriceGross: number; suggestedPriceNet: number } {
  const { cost, targetFoodCostPct, vatPct, commissionPct } = input;
  const c = commissionPct / 100;
  const factor = 1 / (1 + vatPct / 100) - c / (1 + c);

  if (cost <= 0 || factor <= 0) return { suggestedPriceGross: 0, suggestedPriceNet: 0 };

  const suggestedPriceGross = cost / (targetFoodCostPct / 100) / factor;
  return {
    suggestedPriceGross,
    suggestedPriceNet: suggestedPriceGross / (1 + vatPct / 100),
  };
}

function buildAlerts(input: {
  grossMarginPct: number;
  costDeviationPct?: number;
  settings: TenantSettings;
  platform?: DeliveryPlatform;
}): string[] {
  const { grossMarginPct, costDeviationPct, settings, platform } = input;
  const a = settings.alerts;
  const alerts: string[] = [];

  if (costDeviationPct != null) {
    if (costDeviationPct > a.costIncreasePct) {
      alerts.push(
        `El coste ha subido un ${costDeviationPct.toFixed(1)} % respecto al coste de referencia. Revisa precios de proveedor.`,
      );
    } else if (costDeviationPct < -a.costDecreasePct) {
      alerts.push(
        `El coste ha bajado un ${Math.abs(costDeviationPct).toFixed(1)} %. Puedes mejorar margen o precio.`,
      );
    }
  }

  if (grossMarginPct < a.minGrossMarginPct) {
    alerts.push(
      `El margen bruto (${grossMarginPct.toFixed(1)} %) está por debajo del mínimo que te has fijado (${a.minGrossMarginPct} %).`,
    );
  }
  if (platform && grossMarginPct < 0) {
    alerts.push(`Estás perdiendo dinero con cada venta en ${platform.name}.`);
  }

  return alerts;
}

/* ------------------------------------------------------------------ */
/* Canales: PVP por canal y cartas                                     */
/* ------------------------------------------------------------------ */

/**
 * PVP bruto del plato en un canal. Sin canal (comedor) o sin PVP propio,
 * hereda el de la carta general. Un `null` (no se vende alli) tambien
 * hereda: quien pregunta el precio de algo que no se vende ya ha filtrado
 * antes con `availableInChannel`.
 */
export function channelPriceOf(item: MenuItem, platformId?: string): number {
  if (!platformId) return item.sellingPriceGross;
  const own = item.channelPrices?.[platformId];
  // Solo un numero es un PVP propio; null ("no se vende") y undefined heredan.
  return typeof own === 'number' ? own : item.sellingPriceGross;
}

/**
 * ¿Se vende este plato en este canal?
 * - Comedor (sin platformId): los platos de carta general.
 * - Plataforma P: los platos clavados a P mas los generales que no esten
 *   excluidos (channelPrices[P] === null es "fuera de esta carta").
 */
export function availableInChannel(item: MenuItem, platformId?: string): boolean {
  if (!platformId) return !item.platformId;
  if (item.platformId) return item.platformId === platformId;
  return item.channelPrices?.[platformId] !== null;
}

/** Linea de una carta generada. */
export interface MenuCardLine {
  menuItemId: string;
  name: string;
  priceGross: number;
  /** true si el PVP es el heredado de comedor (no hay precio propio). */
  inherited: boolean;
  /** Grupo de carta de la receta: Entrantes, Principales, Postres... */
  group?: string;
}

export interface MenuCardSection {
  /** undefined = platos sin grupo asignado. */
  group?: string;
  lines: MenuCardLine[];
}

/**
 * Orden habitual de una carta. Lo que no encaje va detras en alfabetico, y
 * los platos sin grupo al final del todo: una carta que empieza por los
 * postres no la quiere nadie, y ordenar alfabeticamente los grupos dejaria
 * "Entrantes" detras de "Bebidas".
 */
const ORDEN_CARTA = [
  'aperitivos',
  'para picar',
  'entrantes',
  'ensaladas',
  'sopas',
  'principales',
  'carnes',
  'pescados',
  'arroces',
  'guarniciones',
  'postres',
  'bebidas',
  'vinos',
];

function pesoDeGrupo(group: string | undefined): number {
  if (!group) return ORDEN_CARTA.length + 1;
  const i = ORDEN_CARTA.indexOf(group.trim().toLowerCase());
  return i === -1 ? ORDEN_CARTA.length : i;
}

/**
 * Carta de un canal: platos activos disponibles en el, con su PVP del canal,
 * en orden alfabetico. Es lo que se imprime para Comedor / cada plataforma.
 */
/**
 * El grupo de carta de un plato: manda el del PRODUCTO, la receta es respaldo.
 *
 * Un mismo escandallo puede venderse como cosas distintas —la misma masa es
 * entrante en raciones y principal en pizza— y donde se vende es decision del
 * producto, no de la receta (Alex, 2 sep 2026). Que la receta siga sirviendo
 * de respaldo es lo que permite mover el campo sin migrar nada: los platos que
 * ya estaban dados de alta conservan su grupo hasta que alguien les ponga uno
 * propio, y las recetas que aun no estan en carta siguen teniendo con que
 * filtrarse en Recetas y costeo.
 */
export function menuGroupOf(
  item: Pick<MenuItem, 'group'>,
  grupoDeLaReceta?: string,
): string | undefined {
  return item.group?.trim() || grupoDeLaReceta?.trim() || undefined;
}

export function menuCardFor(
  items: MenuItem[],
  platformId?: string,
  /** Recetas, para el grupo de respaldo de los platos que no lo tengan propio. */
  recipes: Array<Pick<Recipe, 'recipeId' | 'group'>> = [],
): MenuCardLine[] {
  const grupoDe = new Map(recipes.map((r) => [r.recipeId, r.group?.trim() || undefined]));
  return items
    .filter((item) => item.active && availableInChannel(item, platformId))
    .map((item) => ({
      menuItemId: item.menuItemId,
      name: item.name,
      priceGross: round(channelPriceOf(item, platformId), 2),
      inherited:
        Boolean(platformId) &&
        item.platformId !== platformId &&
        item.channelPrices?.[platformId as string] == null,
      group: menuGroupOf(item, grupoDe.get(item.recipeId)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/**
 * Orden manual de la carta, tal y como lo ha dejado el usuario arrastrando.
 *
 * Es COMUN a todos los canales a proposito (decision de Alex, 1 sep 2026): un
 * orden por canal multiplica el mantenimiento y casi nadie quiere su carta de
 * Glovo en otro orden que la del local. Si algun dia hace falta, se anyade
 * por canal sin romper esto.
 *
 * Lo que no aparece en las listas conserva el orden por defecto DETRAS de lo
 * ordenado a mano: asi un plato nuevo no se cuela en medio de la carta ni
 * desaparece, simplemente aparece al final de su grupo.
 */
export interface MenuOrder {
  /** Nombres de grupo, en el orden elegido. */
  groups?: string[];
  /** menuItemId, en el orden elegido. */
  items?: string[];
}

/** Posicion manual, o Infinity si no se ha ordenado a mano. */
const posicionEn = (lista: string[] | undefined, clave: string): number => {
  const i = lista?.indexOf(clave) ?? -1;
  return i === -1 ? Infinity : i;
};

/**
 * La misma carta partida en secciones por grupo, en el orden en el que se
 * lee una carta de verdad. Peticion de Alex (12 ago): la previsualizacion de
 * un listado alfabetico de 40 platos no se parece en nada a su carta.
 *
 * Con `order`, manda lo que el usuario haya arrastrado; sin el, el orden
 * habitual de una carta (ORDEN_CARTA) y alfabetico dentro de cada grupo.
 */
export function menuCardSections(lines: MenuCardLine[], order?: MenuOrder): MenuCardSection[] {
  const secciones = new Map<string, MenuCardSection>();
  for (const line of lines) {
    const clave = line.group ?? '';
    const actual = secciones.get(clave) ?? { group: line.group, lines: [] };
    actual.lines.push(line);
    secciones.set(clave, actual);
  }

  for (const seccion of secciones.values()) {
    seccion.lines.sort((a, b) => {
      const pa = posicionEn(order?.items, a.menuItemId);
      const pb = posicionEn(order?.items, b.menuItemId);
      if (pa !== pb) return pa - pb;
      return a.name.localeCompare(b.name, 'es');
    });
  }

  return [...secciones.values()].sort((a, b) => {
    const ma = posicionEn(order?.groups, a.group ?? '');
    const mb = posicionEn(order?.groups, b.group ?? '');
    if (ma !== mb) return ma - mb;
    const pa = pesoDeGrupo(a.group);
    const pb = pesoDeGrupo(b.group);
    if (pa !== pb) return pa - pb;
    return (a.group ?? '').localeCompare(b.group ?? '', 'es');
  });
}

/**
 * Compara el mismo plato vendido en local y en cada plataforma, cada canal
 * con SU PVP (propio o heredado). Es la vista que hace entender por que el
 * delivery puede no ser rentable. Los canales donde el plato no se vende
 * no aparecen.
 */
export function comparePlatforms(
  item: MenuItem,
  recipeCost: RecipeCost,
  settings: TenantSettings,
): PricingAnalysis[] {
  const inHouse = analyzePricing({ ...item, platformId: undefined }, recipeCost, settings);
  const perPlatform = settings.platforms
    .filter((p) => p.active && availableInChannel(item, p.platformId))
    .map((p) => analyzePricing({ ...item, platformId: p.platformId }, recipeCost, settings));
  return [inHouse, ...perPlatform];
}

/* ------------------------------------------------------------------ */
/* Menu engineering                                                    */
/* ------------------------------------------------------------------ */

/**
 * Recomendacion CORTA de la tabla. No es una orden: la regla de la ronda 3 es
 * que la columna oriente y que Alexia amplie con las reglas de decision de
 * cada cuadrante y los numeros reales del plato (viven en el prompt del
 * asistente).
 */
const RECOMMENDATIONS: Record<MenuEngineeringClass, string> = {
  estrella: 'Protege lo que funciona: mantén calidad y porciones y dale visibilidad.',
  vaca: 'Mejora el margen sin perder popularidad: revisa coste y valora un ajuste de precio.',
  puzzle: 'Intenta vender más antes de descartarlo: visibilidad, nombre o recomendación en sala.',
  perro: 'No lo retires en automático: revisa si cumple una función; si no aporta, plantéatelo.',
};

/**
 * Matriz de Kasavana & Smith: popularidad contra margen de contribucion.
 *
 * La clasificacion se calcula POR GRUPO DE CARTA (ronda 3): la popularidad de
 * un postre solo se compara con la de los otros postres, y los limites de
 * popularidad y de margen se derivan unicamente de su grupo. Un plato sin
 * grupo va al grupo `null` ("Sin grupo"). Cada item devuelve los limites con
 * los que se clasifico, para que la UI pinte las lineas de corte y Alexia
 * pueda explicar el cuadrante con los numeros reales.
 */
export function menuEngineering(
  items: Array<{ item: MenuItem; analysis: PricingAnalysis; group?: string | null }>,
): MenuEngineeringItem[] {
  const withSales = items.filter((x) => (x.item.unitsSold ?? 0) > 0);
  if (withSales.length === 0) return [];

  const byGroup = new Map<string | null, typeof withSales>();
  for (const entry of withSales) {
    const key = entry.group ?? null;
    const list = byGroup.get(key) ?? [];
    list.push(entry);
    byGroup.set(key, list);
  }

  const result: MenuEngineeringItem[] = [];
  for (const [group, groupItems] of byGroup) {
    const totalUnits = groupItems.reduce((s, x) => s + (x.item.unitsSold ?? 0), 0);
    // Regla clasica de Kasavana & Smith: popular = supera el 70 % de la
    // popularidad media del grupo (100 / nº de platos del grupo).
    const popularityThreshold = (100 / groupItems.length) * 0.7;
    const avgMargin =
      groupItems.reduce((s, x) => s + x.analysis.grossMargin * (x.item.unitsSold ?? 0), 0) /
      totalUnits;

    for (const { item, analysis } of groupItems) {
      const unitsSold = item.unitsSold ?? 0;
      const popularityPct = (unitsSold / totalUnits) * 100;
      const popular = popularityPct >= popularityThreshold;
      const profitable = analysis.grossMargin >= avgMargin;
      const classification: MenuEngineeringClass = popular
        ? profitable
          ? 'estrella'
          : 'vaca'
        : profitable
          ? 'puzzle'
          : 'perro';

      result.push({
        menuItemId: item.menuItemId,
        name: item.name,
        group,
        unitsSold,
        popularityPct: round(popularityPct, 2),
        contributionMargin: round(analysis.grossMargin, 2),
        totalContribution: round(analysis.grossMargin * unitsSold, 2),
        popularityThresholdPct: round(popularityThreshold, 2),
        marginThreshold: round(avgMargin, 2),
        classification,
        recommendation: RECOMMENDATIONS[classification],
      });
    }
  }

  return result.sort((a, b) => b.totalContribution - a.totalContribution);
}

/**
 * Recosteo masivo: que platos han cambiado de coste desde la ultima revision.
 * En Excel esto obliga a abrir las 90 pestañas una por una.
 */
export function findCostDeviations(
  items: Array<{ item: MenuItem; cost: RecipeCost }>,
  settings: TenantSettings,
): Array<{ menuItemId: string; name: string; theoreticalCost: number; currentCost: number; deviationPct: number }> {
  return items
    .filter((x) => x.item.theoreticalCost && x.item.theoreticalCost > 0)
    .map((x) => {
      const currentCost = x.cost.costPerPortion;
      const theoreticalCost = x.item.theoreticalCost!;
      return {
        menuItemId: x.item.menuItemId,
        name: x.item.name,
        theoreticalCost: round(theoreticalCost, 4),
        currentCost: round(currentCost, 4),
        deviationPct: round((1 - theoreticalCost / currentCost) * 100, 2),
      };
    })
    .filter(
      (d) =>
        d.deviationPct > settings.alerts.costIncreasePct ||
        d.deviationPct < -settings.alerts.costDecreasePct,
    )
    .sort((a, b) => b.deviationPct - a.deviationPct);
}

/* ------------------------------------------------------------------ */
/* Alertas de la carta                                                 */
/* ------------------------------------------------------------------ */

/**
 * Un aviso de la carta, ya listo para pintar.
 *
 * Existe para que el aviso sea el MISMO en Precios de la carta y en el panel.
 * Antes vivian separados: los margenes y las desviaciones salian en "Avisos de
 * la carta", los platos que no se pueden costear en otra tarjeta distinta, y
 * al panel no llegaba ninguno de los dos. Un plato roto podia estar en carta y
 * en Precios parecer que iba todo bien (Alex, 1 sep 2026).
 *
 * `target` dice a donde lleva el aviso. No al plato por sistema: si la culpa es
 * de una subreceta, al usuario hay que llevarlo alli (ver `CostingCulprit`).
 */
export interface CartaAlert {
  /** Clave estable para React y para deduplicar. */
  id: string;
  /**
   * Clave del aviso a efectos de "Entendido": tipo + plato, SIN el numero.
   * Que no lleve el valor es lo que permite que reconocer el aviso de un
   * margen del 57,2 % siga valiendo cuando manyana marque 57,1 %.
   */
  ackKey: string;
  /**
   * La metrica del aviso: margen bruto en % o desviacion de coste en %.
   * Es lo que se compara para decidir si la cosa ha empeorado.
   */
  value?: number;
  /** Que plato es. */
  name: string;
  /** Que le pasa, en una frase. */
  detail: string;
  /**
   * `problema`: no se puede costear; el plato esta en carta y no sabemos su
   * margen. Es lo mas grave y va primero.
   * `desviacion`: el coste se ha movido mas de lo que toleras.
   * `margen`: el margen bruto esta por debajo de tu limite.
   */
  kind: 'problema' | 'desviacion' | 'margen';
  /** Ruta a la que lleva el aviso. */
  href: string;
  /** Texto del enlace: dice adonde va, no "ver". */
  action: string;
}

export interface CartaAlertInput {
  /** Platos analizados, con sus alertas de margen y desviacion. */
  items: Array<
    Pick<
      PricingAnalysis,
      'menuItemId' | 'name' | 'platformName' | 'alerts' | 'costDeviationPct' | 'grossMarginPct'
    >
  >;
  /** Platos que ni siquiera se han podido costear. */
  skipped?: Array<{
    menuItemId: string;
    name: string;
    reason: string;
    culprit?: CostingCulprit;
  }>;
  /** Avisos que el usuario ya ha reconocido. Ver `alertaSilenciada`. */
  acks?: AlertAck[];
}

/**
 * Cuanto tiene que empeorar un aviso reconocido para volver a salir.
 *
 * Dos puntos porcentuales. Menos que eso es ruido —reconoces un margen del
 * 57,2 % y manyana es 57,1 %— y volver a avisar por ahi convierte la seccion
 * en algo que se ignora. Por encima, la situacion ha cambiado de verdad y hay
 * que decirlo (Alex, 2 sep 2026).
 */
export const TOLERANCIA_AVISO_PCT = 2;

/**
 * ¿Este aviso esta silenciado por un "Entendido" que sigue siendo valido?
 *
 * La regla es la misma para todos: sigue silenciado mientras la cosa NO haya
 * empeorado mas de la tolerancia respecto a como estaba cuando se reconocio.
 * Un problema distinto en el mismo plato es un aviso nuevo y se ve.
 */
function alertaSilenciada(alerta: CartaAlert, ack: AlertAck | undefined): boolean {
  if (!ack) return false;

  // Sin numero (plato que no se puede costear): manda el motivo.
  if (alerta.value == null || ack.value == null) return ack.detail === alerta.detail;

  // El margen empeora BAJANDO; la desviacion de coste, subiendo.
  const empeora =
    alerta.kind === 'margen'
      ? ack.value - alerta.value
      : alerta.value - ack.value;
  return empeora < TOLERANCIA_AVISO_PCT;
}

/**
 * Enlace al plato DENTRO de Precios de la carta.
 *
 * Antes era un `/precios` pelado, y desde la propia pagina de Precios eso es
 * un enlace a ningun sitio: pulsabas "Ver en la carta" y no pasaba nada
 * (Alex, 2 sep 2026). Con el plato en la query, la pantalla puede bajar hasta
 * su fila y resaltarla.
 */
function hrefEnLaCarta(menuItemId: string): string {
  return `/precios?plato=${encodeURIComponent(menuItemId)}`;
}

/**
 * Todo lo que necesita atencion en la carta, en un solo sitio y en orden de
 * gravedad. Lo consumen Precios de la carta y el panel general.
 */
export function cartaAlerts(input: CartaAlertInput): CartaAlert[] {
  const alerts: CartaAlert[] = [];
  const acks = new Map((input.acks ?? []).map((a) => [a.key, a]));

  for (const plato of input.skipped ?? []) {
    // A donde se va a arreglar: casi nunca al plato.
    const href =
      plato.culprit?.kind === 'recipe'
        ? `/recetas/${plato.culprit.id}`
        : plato.culprit?.kind === 'ingredient'
          ? '/ingredientes'
          : hrefEnLaCarta(plato.menuItemId);
    const action = plato.culprit ? `Revisar «${plato.culprit.name}»` : 'Ver en la carta';
    alerts.push({
      id: `problema:${plato.menuItemId}`,
      ackKey: `problema:${plato.menuItemId}`,
      name: plato.name,
      detail: plato.reason,
      kind: 'problema',
      href,
      action,
    });
  }

  for (const item of input.items) {
    const nombre = item.platformName ? `${item.name} · ${item.platformName}` : item.name;
    for (const texto of item.alerts) {
      // El texto del motor ya distingue el caso; aqui solo se clasifica para
      // ordenar y para poder agrupar en el panel.
      const kind: CartaAlert['kind'] = /coste/i.test(texto) ? 'desviacion' : 'margen';
      alerts.push({
        id: `${kind}:${item.menuItemId}:${texto}`,
        ackKey: `${kind}:${item.menuItemId}`,
        value: kind === 'margen' ? item.grossMarginPct : item.costDeviationPct,
        name: nombre,
        detail: texto,
        kind,
        href: hrefEnLaCarta(item.menuItemId),
        action: 'Ver en la carta',
      });
    }
  }

  const peso = { problema: 0, desviacion: 1, margen: 2 } as const;
  return alerts
    .filter((alerta) => !alertaSilenciada(alerta, acks.get(alerta.ackKey)))
    .sort((a, b) => peso[a.kind] - peso[b.kind] || a.name.localeCompare(b.name, 'es'));
}
